// Sticky right-hand live estimate + line items + wallet payoff.

import { Link } from "react-router-dom";
import { FileThumb } from "@/components/UploadDropzone";
import { ksh } from "@/lib/api";
import { ORDER } from "@/constants/testIds";

export function OrderSummary({
  items,
  itemTotals,
  pricing,
  subtotal,
  tier,
  mult,
  tieredSubtotal,
  fee,
  total,
  business,
  businessDiscountPct,
  businessDiscountAmount,
  totalAfterBusiness,
  walletBalance,
  walletAfter,
  willPayFromWallet,
}) {
  return (
    <div className="lg:col-span-4">
      <div className="lg:sticky lg:top-24 bg-white border border-[#0A0A0A]" data-testid={ORDER.costSummary}>
        <div className="bg-[#0A0A0A] text-white p-5">
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold opacity-70">Live Estimate</div>
          <div className="font-display font-black text-4xl mt-1" data-testid={ORDER.orderTotal}>{ksh(totalAfterBusiness)}</div>
          <div className="text-xs opacity-70 mt-1">
            {business && willPayFromWallet
              ? "Pays from your Office Pack wallet"
              : "Includes all selected options"}
          </div>
        </div>
        <div className="p-5 space-y-4">
          {items.length === 0 ? (
            <div className="text-sm text-[#525252]">Upload files to see your estimate.</div>
          ) : (
            <>
              <ul className="space-y-3 text-sm">
                {items.map((x, idx) => (
                  <li key={x.file.id} className="flex items-start gap-3 border-b border-[#E5E5E5] pb-3 last:border-b-0">
                    <FileThumb file={x.file} size={32} />
                    <div className="min-w-0 flex-1">
                      <div className="font-bold truncate text-xs">{x.file.original_name}</div>
                      <div className="text-[10px] text-[#525252] mono">
                        {x.spec.copies}× · {x.spec.paper_size} · {x.spec.color_mode === "color" ? "Color" : "B&W"} · {x.file.pages}pg
                        {x.spec.double_sided && " · 2-sided"}
                        {x.spec.binding !== "none" && ` · ${x.spec.binding} (${x.spec.binding_cover || "blue"})`}
                        {x.spec.lamination && " · laminated"}
                        {x.spec.stapling && " · stapled"}
                        {x.spec.batch_copies && ` · batched (${x.file.pages * x.spec.copies}pg)`}
                      </div>
                    </div>
                    <div className="mono font-bold whitespace-nowrap text-sm">{ksh(itemTotals[idx])}</div>
                  </li>
                ))}
              </ul>
              <div className="flex items-center justify-between pt-2 text-sm">
                <span className="text-[#525252]">Items subtotal</span>
                <span className="mono font-bold">{ksh(subtotal)}</span>
              </div>
              {mult !== 1 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#525252]">{(pricing.tiers?.[tier]?.label || tier)} surcharge (×{mult})</span>
                  <span className="mono font-bold text-[#DC2626]">+{ksh(tieredSubtotal - subtotal)}</span>
                </div>
              )}
              {fee > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#525252]">Convenience fee</span>
                  <span className="mono font-bold">{ksh(fee)}</span>
                </div>
              )}
              {subtotal + fee < (pricing.min_order || 0) && (
                <div className="flex items-center justify-between text-xs text-[#525252]">
                  <span>Min order applies</span>
                  <span className="mono">{ksh(pricing.min_order)}</span>
                </div>
              )}
              {business && businessDiscountAmount > 0 && (
                <div className="flex items-center justify-between text-sm" data-testid="order-business-discount">
                  <span className="text-[#525252]">
                    {business.account.name} · {businessDiscountPct}% off
                  </span>
                  <span className="mono font-bold text-[#059669]">-{ksh(businessDiscountAmount)}</span>
                </div>
              )}
              <div className="flex items-center justify-between pt-3 border-t-2 border-[#0A0A0A]">
                <span className="font-bold uppercase text-xs tracking-[0.15em]">Total</span>
                <span className="mono font-display font-black text-xl">{ksh(totalAfterBusiness)}</span>
              </div>
              {business && (
                <div
                  className="mt-3 border border-[#0A0A0A] bg-[#FAFAFA] p-3"
                  data-testid="order-wallet-payoff"
                >
                  <div className="text-[9px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-1">
                    § Office Pack wallet
                  </div>
                  {willPayFromWallet ? (
                    <>
                      <div className="text-xs text-[#0A0A0A]">
                        This order will <span className="font-bold text-[#059669]">auto-settle from your wallet</span> — no M-Pesa PIN needed.
                      </div>
                      <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <div className="text-[9px] uppercase tracking-[0.2em] text-[#525252]">Before</div>
                          <div className="mono font-bold">{ksh(walletBalance)}</div>
                        </div>
                        <div>
                          <div className="text-[9px] uppercase tracking-[0.2em] text-[#525252]">After</div>
                          <div className="mono font-bold text-[#059669]">{ksh(walletAfter)}</div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="text-xs text-[#92400E]">
                        Wallet balance <span className="mono font-bold">{ksh(walletBalance)}</span> is short of <span className="mono font-bold">{ksh(totalAfterBusiness)}</span> — you&apos;ll be charged via M-Pesa STK.
                      </div>
                      <Link to="/dashboard" className="inline-block mt-1 text-[10px] uppercase tracking-[0.2em] font-bold text-[#059669] hover:underline">
                        Top up first →
                      </Link>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
